import { ActionWithPayload } from '@/types/redux';
import { createSelector } from 'reselect';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '@/redux/store';
import { handleActions } from 'redux-actions';
import { LOAD_HOUSE_TERMS_FAILURE, LOAD_HOUSE_TERMS_REQUEST, LOAD_HOUSE_TERMS_SUCCESS } from './actions';
import { Reducer } from 'redux';
import api from '../api/houseTerms';
import ms from 'ms';

const REDUX_STORE_TIME_ITEM = ms('20s');

type HouseTermsSlice = {
    error: boolean;
    houseTerms: any[];
    loaded: Date;
    loading: boolean;
};

export const defaultHouseTermsSlice: HouseTermsSlice = {
    error: undefined,
    houseTerms: [],
    loaded: undefined,
    loading: false,
};

export const reducer: Reducer<HouseTermsSlice> = handleActions(
    {
        [LOAD_HOUSE_TERMS_FAILURE]: (
            state: HouseTermsSlice,
            action: ActionWithPayload<{ error: string }>
        ): HouseTermsSlice => {
            return {
                ...state,
                error: action.error,
                loading: false,
            };
        },
        [LOAD_HOUSE_TERMS_REQUEST]: (state: HouseTermsSlice): HouseTermsSlice => ({
            ...state,
            loading: true,
        }),
        [LOAD_HOUSE_TERMS_SUCCESS]: (state: HouseTermsSlice, action: ActionWithPayload<any>): HouseTermsSlice => ({
            ...state,
            houseTerms: [...action.payload],
            loaded: new Date(),
            loading: false,
        }),
    },
    defaultHouseTermsSlice
);

const stateSelector = (state: GlobalState) => state.houseTerms;

export const getHouseTerms = createSelector(stateSelector, (state) => state.houseTerms);

export const getHouseTermsLoading = createSelector(stateSelector, (state) => state.loading);

export const getHouseTermsLoaded = createSelector(stateSelector, (state) => state.loaded);

export const shouldFetchHouseTerms = createSelector(
    getHouseTermsLoaded,
    getHouseTermsLoading,
    (houseTermsLoaded, houseTermsLoading) => {
        if (houseTermsLoading) {
            return false;
        }
        const time = Date.now();
        const diff = time - (houseTermsLoaded?.valueOf() || 0);
        return !houseTermsLoaded || diff > REDUX_STORE_TIME_ITEM;
    }
);

export const fetchHouseTermsIfNeeded = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldFetchHouseTerms(state)) {
        try {
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            dispatch({ type: LOAD_HOUSE_TERMS_REQUEST });

            const response = await api.getHouseTerms({
                authToken,
                deployment,
            });

            return dispatch({
                payload: response.payload,
                type: LOAD_HOUSE_TERMS_SUCCESS,
            });
        } catch (error) {
            return dispatch({
                error: true,
                payload: error.message,
                type: LOAD_HOUSE_TERMS_FAILURE,
            });
        }
    }
};
